import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { adminClient, auditQuery, formatAdminDate } from "@/api/adminClient";
import { AdminListPager } from "@/components/admin/AdminListToolbar";

const emptyFilters = {
  q: "",
  action: "",
  organisation_id: "",
  actor_id: "",
  from: "",
  to: "",
  limit: "25",
  offset: "0",
};

function filtersFromQuery(query) {
  const next = Object.fromEntries(
    Object.entries(emptyFilters).map(([key, fallback]) => [
      key,
      query.get(key) ?? fallback,
    ]),
  );
  for (const key of ["from", "to"]) {
    if (!next[key]) continue;
    const date = new Date(next[key]);
    if (Number.isFinite(date.getTime())) {
      next[key] = date.toISOString().slice(0, -1);
    }
  }
  return next;
}

export default function AdminAudit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryKey = searchParams.toString();
  const initialFilters = filtersFromQuery(searchParams);
  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const generation = useRef(0);
  const abortRef = useRef(null);

  async function load(nextFilters = applied) {
    const current = ++generation.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState("loading");
    setError("");
    try {
      const query = auditQuery({ ...nextFilters, paged: "true" });
      const result = await adminClient(
        `/admin/audit${query ? `?${query}` : ""}`,
        { signal: controller.signal },
      );
      if (current !== generation.current) return;
      setRows(result.items || []);
      setTotal(Number(result.total) || 0);
      setState("ready");
    } catch (e) {
      if (e.name === "AbortError" || current !== generation.current) return;
      setError(e.message);
      setState("error");
    }
  }

  useEffect(() => {
    const next = filtersFromQuery(new URLSearchParams(queryKey));
    setFilters(next);
    setApplied(next);
    setRows([]);
    load(next);
    return () => {
      generation.current += 1;
      abortRef.current?.abort();
    };
  }, [queryKey]);

  function update(key, value) {
    setFilters((old) => ({ ...old, [key]: value, ...(key !== "offset" ? { offset: "0" } : {}) }));
  }
  function submit(e) {
    e.preventDefault();
    try {
      const nextQuery = auditQuery(filters);
      if (nextQuery === queryKey) {
        setApplied({ ...filters });
        load(filters);
      } else setSearchParams(nextQuery);
    } catch (e) {
      setError(e.message);
      setState("error");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Audit</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Search provider activity by action, organisation, operator, and UTC
          time boundaries.
        </p>
      </div>
      <form
        onSubmit={submit}
        className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2 xl:grid-cols-3"
        aria-label="Audit filters"
      >
        <label className="text-sm font-semibold">
          Search activity or object reference
          <input type="search" maxLength={200} value={filters.q}
            onChange={(e) => update("q", e.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal" />
        </label>
        <label className="text-sm font-semibold">
          Action
          <input
            value={filters.action}
            onChange={(e) => update("action", e.target.value)}
            placeholder="content.resource.create"
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
          />
        </label>
        <label className="text-sm font-semibold">
          Organisation
          <input
            value={filters.organisation_id}
            onChange={(e) => update("organisation_id", e.target.value)}
            placeholder="global or organisation identifier"
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
          />
        </label>
        <label className="text-sm font-semibold">
          Operator
          <input
            value={filters.actor_id}
            onChange={(e) => update("actor_id", e.target.value)}
            placeholder="operator identifier"
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
          />
        </label>
        <label className="text-sm font-semibold">
          From (UTC)
          <input
            type="datetime-local"
            step="any"
            value={filters.from}
            onChange={(e) => update("from", e.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
          />
        </label>
        <label className="text-sm font-semibold">
          To (UTC)
          <input
            type="datetime-local"
            step="any"
            value={filters.to}
            onChange={(e) => update("to", e.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
          />
        </label>
        <label className="text-sm font-semibold">
          Rows
          <input
            type="number"
            min="1"
            max="500"
            step="1"
            value={filters.limit}
            onChange={(e) => update("limit", e.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
          />
        </label>
        <div className="flex flex-wrap items-end gap-2 sm:col-span-2 xl:col-span-3">
          <button
            type="submit"
            className="min-h-11 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800"
          >
            Apply filters
          </button>
          <button
            type="button"
            onClick={() => {
              if (queryKey) setSearchParams({});
              else {
                setFilters(emptyFilters);
                setApplied(emptyFilters);
                load(emptyFilters);
              }
            }}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
          >
            Clear
          </button>
        </div>
      </form>
      {state === "idle" && (
        <p className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          Apply filters to load the audit stream.
        </p>
      )}
      {state === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
        >
          Loading audit events…
        </p>
      )}
      {state === "error" && (
        <div
          className="rounded-lg border border-destructive/40 bg-card p-5"
          role="alert"
        >
          <p className="text-sm text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => load(applied)}
            className="mt-3 min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted"
          >
            Retry current filters
          </button>
        </div>
      )}
      {rows.length > 0 && state !== "idle" && (
        <div
          role="region"
          aria-label="Audit events table"
          tabIndex={0}
          className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
          <table className="w-full min-w-[760px] table-fixed text-left text-sm">
            <caption className="sr-only">Audit events</caption>
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Organisation</th>
                <th className="px-4 py-3">Operator</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border align-top last:border-0"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {formatAdminDate(row.created_at)}
                  </td>
                  <th scope="row" className="px-4 py-3 font-semibold">
                    {row.action}
                  </th>
                  <td className="px-4 py-3 font-mono text-xs">
                    {row.organisation_id || "global"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {row.actor_id || "system"}
                  </td>
                  <td className="px-4 py-3">
                    {row.entity_type || "—"}
                    {row.entity_id && (
                      <span className="mt-1 block font-mono text-xs text-muted-foreground">
                        {row.entity_id}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {row.ip || "—"}
                  </td>
                  <td className="w-[24%] whitespace-pre-wrap break-words px-4 py-3 text-muted-foreground">
                    {row.detail || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {state === "ready" && rows.length === 0 && (
        <p className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No audit events match these filters.
        </p>
      )}
      {state === "ready" && <AdminListPager offset={Number(applied.offset || 0)} limit={Number(applied.limit || 25)} total={total} onPage={(offset) => setSearchParams((current) => { const next = new URLSearchParams(current); next.set("offset", String(offset)); return next; })} />}
    </div>
  );
}
